// Sprint 3.B: servicio para listar y reproducir llamadas grabadas
// desde el modulo KYE Call Center Desktop.
//
// El doc llamadas/{callId} es creado por la Cloud Function procesarLlamada
// cuando la app desktop sube el WAV a Storage. Aqui solo leemos y
// resolvemos la URL publica para reproducir.

import {
  collection,
  query,
  where,
  orderBy,
  limit as fbLimit,
  onSnapshot,
} from "firebase/firestore";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";
import { db } from "../Firebase/firebaseconfig";

const COLLECTION = "llamadas";

/**
 * Suscribe a las llamadas de una deuda especifica, ordenadas por fecha
 * descendente. Devuelve unsubscribe.
 *
 * Requiere que la llamada haya sido grabada con deudaId predeterminada
 * (desde Sprint 3.A.2 el desktop pide deuda ANTES de grabar). Llamadas
 * creadas antes de Sprint 3.A.2 con deudaId=null no aparecen aqui;
 * quedan accesibles via ClienteDetalle (todas las llamadas del cliente
 * sin importar deudaId).
 */
export function escucharLlamadasPorDeuda(deudaId, callback, max = 50) {
  if (!deudaId) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(db, COLLECTION),
    where("deudaId", "==", deudaId),
    orderBy("creadoEn", "desc"),
    fbLimit(max),
  );
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    },
    (err) => {
      console.error("[llamadasService] onSnapshot por deuda:", err);
      callback([]);
    },
  );
}

/**
 * Suscribe a las llamadas de un cliente especifico (incluye todas sus
 * deudas + las marcadas sin deuda). Ordenado por fecha desc.
 */
export function escucharLlamadasPorCliente(clienteId, callback, max = 100) {
  if (!clienteId) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(db, COLLECTION),
    where("clienteId", "==", clienteId),
    orderBy("creadoEn", "desc"),
    fbLimit(max),
  );
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    },
    (err) => {
      console.error("[llamadasService] onSnapshot por cliente:", err);
      callback([]);
    },
  );
}

/**
 * Convierte un path gs://<bucket>/<obj> en una URL HTTPS firmada para
 * reproducir en <audio>. El SDK valida que el caller tenga read access
 * via Storage rules.
 *
 * Cache en memoria por audioPath para no resolver la URL multiples veces.
 */
const urlCache = new Map();

export async function getAudioPlayableUrl(audioPath) {
  if (!audioPath) return null;
  if (urlCache.has(audioPath)) return urlCache.get(audioPath);

  const storage = getStorage();
  // El path puede venir como gs://bucket/llamadas/...wav o solo llamadas/...wav
  let ref;
  if (audioPath.startsWith("gs://")) {
    ref = storageRef(storage, audioPath);
  } else {
    ref = storageRef(storage, audioPath);
  }
  const url = await getDownloadURL(ref);
  urlCache.set(audioPath, url);
  return url;
}

/**
 * Helper para formatear duracion en mm:ss (la guardamos en segundos).
 */
export function formatDuracion(segundos) {
  const s = Math.max(0, Math.round(Number(segundos) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}
